import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import type { OutreachSourceType } from "features/integrations/types";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { del, post } from "common/helpers/HTTP";

import type {
  CrmLeadListSource,
  EventLeadListSource,
  GroupLeadListSource,
  LeadList,
  LeadListSource,
  ListLeadListSource,
  PostLeadListSource,
} from "../types";

type CreateSourceParams =
  | {
      settings: (
        | GroupLeadListSource
        | PostLeadListSource
        | EventLeadListSource
      )["settings"];
      miner: string;
    }
  | {
      settings: CrmLeadListSource["settings"];
      crm_type: OutreachSourceType;
    }
  | { settings: ListLeadListSource["settings"] };

async function createSourceRequest(
  workspaceId: string,
  listId: string,
  params: CreateSourceParams,
): Promise<LeadListSource> {
  return (
    await post(`workspaces/${workspaceId}/lists/${listId}/source`, {
      ...params,
      type: params.settings.type,
    })
  ).source;
}

async function deleteLeadListSourceRequest(
  workspaceId: string,
  listId: string,
  sourceId: string,
) {
  await del(`workspaces/${workspaceId}/lists/${listId}/source/${sourceId}`);
}

function addCachedSource(list: LeadList, source: LeadListSource): LeadList {
  return {
    ...list,
    sources: [...list.sources, source],
  };
}

function removeCachedSource(list: LeadList, sourceId: string): LeadList {
  return {
    ...list,
    sources: list.sources.filter((source) => source.id !== sourceId),
  };
}

export default function useLeadListSourceMutations() {
  const queryClient = useQueryClient();
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { listsKeys } = getQueryKeys(workspaceId);

  const { mutateAsync: createSource, isPending: isCreatingSource } =
    useMutation({
      mutationFn: ({
        listId,
        params,
      }: {
        listId: string;
        params: CreateSourceParams;
      }) => createSourceRequest(workspaceId, listId, params),
      onSuccess: (newSource, { listId }) => {
        queryClient.setQueryData<LeadList[]>(
          listsKeys.list,
          (prevLists: LeadList[]) =>
            prevLists
              ? prevLists.map((list) =>
                  list.id === listId ? addCachedSource(list, newSource) : list,
                )
              : undefined,
        );
        queryClient.setQueryData<LeadList>(
          listsKeys.details(listId),
          (prevList) =>
            prevList ? addCachedSource(prevList, newSource) : prevList,
        );
      },
    });

  const { mutateAsync: deleteSource, isPending: isDeletingSource } =
    useMutation({
      mutationFn: ({
        listId,
        sourceId,
      }: {
        listId: string;
        sourceId: string;
      }) => deleteLeadListSourceRequest(workspaceId, listId, sourceId),
      onSuccess: (_, { listId, sourceId }) => {
        queryClient.setQueryData<LeadList[]>(listsKeys.list, (prevLists) =>
          prevLists
            ? prevLists.map((list) =>
                list.id === listId ? removeCachedSource(list, sourceId) : list,
              )
            : undefined,
        );
        queryClient.setQueryData<LeadList>(
          listsKeys.details(listId),
          (prevList) =>
            prevList ? removeCachedSource(prevList, sourceId) : prevList,
        );
        toast.success("Source deleted");
      },
      onError: () => toast.error("Failed to delete source"),
    });

  return {
    createSource,
    isCreatingSource,
    deleteSource,
    isDeletingSource,
  };
}
